import React, { useState, useEffect, useMemo } from "react";
import { BottomSheet } from "../../../shared/components/BottomSheet.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { formatAppDate } from "../../../shared/utils/dateUtils.js";
import { maskAccountNumber } from "../../companies/models/company.js";
import { addBankAccountToCompany } from "../../companies/services/companyService.js";
import { readPdfPages, parseStatement, detectAccount, detectColumnCandidates, buildDiagnostics } from "../utils/statementPdf.js";
import { profileForBank } from "../utils/bankProfiles.js";
import { detectBank } from "../utils/bankRegistry.js";
import {
  sha256Hex,
  getImportByHash,
  matchAccount,
  fingerprintRows,
  existingKeys,
  findPossibleDuplicates,
  importTransactions,
  getSavedBankProfile,
  saveBankProfile
} from "../services/bankService.js";

const MAP_KEYS = [
  ["", "— ignore —"],
  ["date", "Date"],
  ["description", "Description"],
  ["ref", "Cheque / Ref"],
  ["debit", "Debit / Withdrawal"],
  ["credit", "Credit / Deposit"],
  ["amount", "Amount (one column)"],
  ["type", "Dr / Cr"],
  ["balance", "Balance"]
];

const rupees = (paise) => (paise === null || paise === undefined ? "" : `₹${(paise / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const errBox = { background: "rgba(239, 68, 68, 0.12)", border: "1px solid rgba(239, 68, 68, 0.35)", padding: "0.6rem 0.85rem", borderRadius: "var(--radius-md)", color: "#fca5a5", fontSize: "0.85rem" };
const okBox = { background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.35)", color: "var(--success)", padding: "0.6rem 0.85rem", borderRadius: "var(--radius-md)", fontSize: "0.85rem" };

/**
 * Import a bank statement PDF: read it on the device (never uploaded), match the account
 * (or ask), review new / already-imported / possible-duplicate rows, then save the data.
 */
export function StatementImportSheet({ isOpen, onClose, companies = [], dateFormat = "DD-MM-YYYY", onImported, refetchCompanies }) {
  const [step, setStep] = useState("pick"); // pick | password | reading | choose | review | done
  const [file, setFile] = useState(null);
  const [buffer, setBuffer] = useState(null);
  const [fileHash, setFileHash] = useState("");
  const [password, setPassword] = useState("");
  const [progress, setProgress] = useState("");
  const [error, setError] = useState("");
  const [parsed, setParsed] = useState(null);
  const [target, setTarget] = useState(null); // { company, account }
  const [choice, setChoice] = useState("");
  const [newAcc, setNewAcc] = useState({ companyId: "", bankName: "", accountNumber: "", ifscCode: "", branchName: "" });
  const [review, setReview] = useState(null); // { keys, existing:Set, possible:Set }
  const [includePossible, setIncludePossible] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [pages, setPages] = useState(null);
  const [detected, setDetected] = useState(null); // account details found even when the table isn't
  const [bank, setBank] = useState(null); // { code, name, confidence } — detected before choosing the extractor
  const [candidates, setCandidates] = useState([]);
  const [mapping, setMapping] = useState({});
  const [rememberLayout, setRememberLayout] = useState(true);
  const [diagnostics, setDiagnostics] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setStep("pick"); setFile(null); setBuffer(null); setFileHash(""); setPassword(""); setProgress("");
    setError(""); setParsed(null); setTarget(null); setChoice(""); setReview(null); setIncludePossible(new Set()); setResult(null);
    setPages(null); setDetected(null); setBank(null); setCandidates([]); setMapping({}); setDiagnostics(null); setCopied(false);
  }, [isOpen]);

  const fmt = (d) => formatAppDate(d, dateFormat);
  const { all: allAccounts } = useMemo(() => matchAccount(companies, {}), [companies]);

  const readFile = async (buf, pwd) => {
    setStep("reading");
    setError("");
    try {
      const readPages = await readPdfPages(new Uint8Array(buf.slice(0)), {
        password: pwd,
        onProgress: (n, total) => setProgress(`Reading page ${n} of ${total}…`)
      });
      setPages(readPages);
      // 1. Which bank is this? 2. Use that bank's extractor profile (+ any layout saved for it)
      const found = detectAccount(readPages);
      setDetected(found);
      const detectedBank = detectBank(readPages);
      setBank(detectedBank);
      setProgress(`Reading as ${detectedBank.name} statement…`);
      const saved = await getSavedBankProfile(detectedBank.code).catch(() => null);
      let result;
      try {
        result = parseStatement(readPages, { profile: profileForBank(detectedBank.code, saved), bank: detectedBank });
      } catch (parseErr) {
        if (parseErr.code === "NO_TABLE" && !/credit-card/i.test(parseErr.message)) {
          setDiagnostics(parseErr.diagnostics || buildDiagnostics(readPages));
          setCandidates(detectColumnCandidates(readPages));
          setMapping({});
          setStep("map");
          return;
        }
        throw parseErr;
      }
      await continueWithParsed(result);
    } catch (err) {
      if (err.code === "NEED_PASSWORD" || err.code === "WRONG_PASSWORD") {
        setStep("password");
        if (err.code === "WRONG_PASSWORD") setError("Wrong PDF password. Try again.");
      } else {
        setStep("pick");
        setError(err.message || "Could not read this PDF.");
      }
    }
  };

  // After a successful read: match the account automatically, or ask which one it is
  const continueWithParsed = async (result) => {
    setParsed(result);
    const { match } = matchAccount(companies, result.account);
    if (match) {
      setTarget(match);
      await prepareReview(match, result);
      return;
    }
    setNewAcc({
      companyId: companies.find((c) => c.isDefault)?.id || companies[0]?.id || "",
      bankName: result.account.bankName,
      accountNumber: result.account.number.replace(/[^0-9]/g, "").length >= 6 ? result.account.number.replace(/\s+/g, "") : "",
      ifscCode: result.account.ifsc,
      branchName: ""
    });
    setChoice("new");
    setStep("choose");
  };

  // "Map the columns" fallback: read with the user's mapping, optionally remember it for this bank
  const applyMapping = async () => {
    setError("");
    const columns = candidates
      .map((c, i) => ({ key: mapping[i], x: c.x, right: c.right }))
      .filter((c) => c.key);
    const keys = columns.map((c) => c.key);
    if (!keys.includes("date") || !(keys.includes("balance") || (keys.includes("debit") && keys.includes("credit")) || keys.includes("amount"))) {
      setError("Choose at least the Date column, and Balance or Debit + Credit (or Amount).");
      return;
    }
    if (new Set(keys).size !== keys.length) {
      setError("Each kind of column can be chosen only once.");
      return;
    }
    try {
      const profile = { ...(profileForBank(bank?.code) || {}), columns };
      const result = parseStatement(pages, { profile, bank });
      if (rememberLayout && bank && bank.code !== "GENERIC") await saveBankProfile(bank.code, { columns }).catch(() => {});
      await continueWithParsed(result);
    } catch (err) {
      setError(err.message || "Still couldn't read the rows with this mapping.");
    }
  };

  const copyDiagnostics = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify({ bank: detected?.bankName || "", ifscPrefix: (detected?.ifsc || "").slice(0, 4), ...diagnostics }, null, 1));
      setCopied(true);
    } catch (_) {
      setCopied(false);
    }
  };

  const handleFile = async (e) => {
    const picked = e.target.files?.[0];
    e.target.value = "";
    if (!picked) return;
    setError("");
    setFile(picked);
    const buf = await picked.arrayBuffer();
    setBuffer(buf);
    const hash = await sha256Hex(buf);
    setFileHash(hash);
    const already = await getImportByHash(hash).catch(() => null);
    if (already) {
      const company = companies.find((c) => c.id === already.companyId);
      const when = already.importedAt?.toDate ? fmt(already.importedAt.toDate().toISOString().slice(0, 10)) : "";
      setError(`This file was already imported${when ? ` on ${when}` : ""} (${already.added} transactions) into ${company?.name || "a company"} • ${maskAccountNumber(already.accountNumber)}. Nothing to import.`);
      return;
    }
    await readFile(buf, "");
  };

  const prepareReview = async (tgt, data = parsed) => {
    setStep("reading");
    setProgress("Checking for transactions already imported…");
    const keys = await fingerprintRows(tgt.account.id, data.rows, data.hasBalance);
    const existing = await existingKeys(keys);
    const possible = await findPossibleDuplicates(tgt.account.id, data.rows, keys, existing).catch(() => new Set());
    setReview({ keys, existing, possible });
    setIncludePossible(new Set());
    setStep("review");
  };

  const confirmChoice = async () => {
    setError("");
    if (choice === "new") {
      const company = companies.find((c) => c.id === newAcc.companyId);
      if (!company) return setError("Choose the company this account belongs to.");
      if (!newAcc.bankName || !newAcc.accountNumber) return setError("Enter the bank name and account number.");
      setBusy(true);
      try {
        const { account } = await addBankAccountToCompany(company, newAcc);
        if (refetchCompanies) await refetchCompanies();
        const tgt = { company, account };
        setTarget(tgt);
        await prepareReview(tgt);
      } catch (err) {
        setError(err.message || "Could not add the account.");
      } finally {
        setBusy(false);
      }
      return;
    }
    const tgt = allAccounts.find((x) => x.account.id === choice);
    if (!tgt) return setError("Choose an account.");
    setTarget(tgt);
    await prepareReview(tgt);
  };

  const selected = useMemo(() => {
    if (!review || !parsed) return [];
    return parsed.rows
      .map((row, i) => ({ row, key: review.keys[i], i }))
      .filter(({ key, i }) => !review.existing.has(key) && (!review.possible.has(i) || includePossible.has(i)));
  }, [review, parsed, includePossible]);

  const doImport = async () => {
    if (!selected.length) return;
    setBusy(true);
    setError("");
    try {
      const res = await importTransactions({
        company: target.company,
        account: target.account,
        parsed,
        rows: selected.map((s) => s.row),
        keys: selected.map((s) => s.key),
        fileName: file?.name || "statement.pdf",
        fileHash,
        duplicates: parsed.rows.length - selected.length
      });
      setResult(res);
      setStep("done");
      const dates = parsed.rows.map((r) => r.date).sort();
      if (onImported) onImported(target, { from: parsed.period.from || dates[0] || "", to: parsed.period.to || dates[dates.length - 1] || "" });
    } catch (err) {
      setError(err.message || "Import failed.");
    } finally {
      setBusy(false);
    }
  };

  const alreadyCount = review ? parsed.rows.filter((_, i) => review.existing.has(review.keys[i])).length : 0;
  const possibleCount = review ? review.possible.size : 0;

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="⬆️ Import bank statement" size="lg">
      <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
        {file && (
          <div style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>
            📄 {file.name}
            {bank && <div style={{ fontWeight: 700, color: "var(--text-main)" }}>🏦 Detected: {bank.name}{bank.code !== "GENERIC" ? ` (${bank.code})` : ""}</div>}
          </div>
        )}
        {error && <div style={errBox}>⚠️ {error}</div>}

        {step === "pick" && (
          <>
            <div style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>
              Choose the statement PDF downloaded from net-banking. It is read on this device only — the PDF is not uploaded or stored; only the transactions are saved.
            </div>
            <label className="btn btn-primary" style={{ cursor: "pointer", textAlign: "center" }}>
              📄 Choose PDF
              <input type="file" accept="application/pdf,.pdf" style={{ display: "none" }} onChange={handleFile} />
            </label>
          </>
        )}

        {step === "password" && (
          <form onSubmit={(e) => { e.preventDefault(); readFile(buffer, password); }} style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            <div style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>🔒 This PDF is password protected (banks often use your customer ID or date of birth).</div>
            <input type="password" className="form-control" placeholder="PDF password" value={password} autoFocus onChange={(e) => setPassword(e.target.value)} />
            <Button type="submit" variant="primary">Unlock</Button>
          </form>
        )}

        {step === "reading" && <div style={{ textAlign: "center", color: "var(--text-dim)", padding: "1.5rem" }}>⏳ {progress || "Reading the statement…"}</div>}

        {step === "choose" && parsed && (
          <>
            <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", padding: "0.7rem 0.85rem", fontSize: "0.85rem" }}>
              <div style={{ fontWeight: 700 }}>Found in the PDF</div>
              <div>{parsed.account.bankName || "Bank"} • A/C {parsed.account.number || "not shown"}</div>
              {parsed.account.ifsc && <div>IFSC {parsed.account.ifsc}</div>}
              <div style={{ color: "var(--text-dim)" }}>{fmt(parsed.period.from)} → {fmt(parsed.period.to)} • {parsed.rows.length} transactions</div>
            </div>
            <div style={{ fontWeight: 700, fontSize: "0.9rem" }}>This account isn't set up yet. Which account is this?</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
              {allAccounts.map(({ company, account }) => (
                <label key={account.id} style={{ display: "flex", gap: "0.5rem", alignItems: "center", fontSize: "0.85rem", cursor: "pointer" }}>
                  <input type="radio" name="stmt-account" checked={choice === account.id} onChange={() => setChoice(account.id)} />
                  {company.name} • {account.bankName} {maskAccountNumber(account.accountNumber)}
                </label>
              ))}
              <label style={{ display: "flex", gap: "0.5rem", alignItems: "center", fontSize: "0.85rem", cursor: "pointer", fontWeight: 700 }}>
                <input type="radio" name="stmt-account" checked={choice === "new"} onChange={() => setChoice("new")} />
                ➕ Add as a new account
              </label>
            </div>
            {choice === "new" && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
                <label style={{ gridColumn: "1 / -1", fontSize: "0.75rem", color: "var(--text-dim)" }}>
                  Company
                  <select className="form-control" value={newAcc.companyId} onChange={(e) => setNewAcc((a) => ({ ...a, companyId: e.target.value }))}>
                    {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </label>
                {[["bankName", "Bank name"], ["accountNumber", "Account number"], ["ifscCode", "IFSC"], ["branchName", "Branch"]].map(([k, label]) => (
                  <label key={k} style={{ fontSize: "0.75rem", color: "var(--text-dim)" }}>
                    {label}
                    <input className="form-control" value={newAcc[k]} onChange={(e) => setNewAcc((a) => ({ ...a, [k]: k === "ifscCode" ? e.target.value.toUpperCase() : e.target.value }))} />
                  </label>
                ))}
              </div>
            )}
            <div style={{ display: "flex", gap: "0.75rem" }}>
              <Button type="button" variant="secondary" onClick={onClose} style={{ flex: 1 }}>Cancel</Button>
              <Button type="button" variant="primary" onClick={confirmChoice} loading={busy} style={{ flex: 1 }}>Continue</Button>
            </div>
          </>
        )}

        {step === "map" && (
          <>
            <div style={{ fontSize: "0.88rem", color: "var(--text-main)", fontWeight: 700 }}>
              We couldn't recognise this bank's layout{detected?.bankName ? ` (${detected.bankName})` : ""}. Tell us which column is which:
            </div>
            {candidates.length === 0 ? (
              <div style={errBox}>⚠️ No table with dates was found in this PDF.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.45rem", maxHeight: "45vh", overflowY: "auto" }}>
                {candidates.map((c, i) => (
                  <div key={i} style={{ display: "flex", gap: "0.5rem", alignItems: "center", background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", padding: "0.45rem 0.6rem" }}>
                    <div style={{ flex: 1, minWidth: 0, fontSize: "0.75rem", color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {c.samples.join("  •  ")}
                    </div>
                    <select className="form-control" style={{ width: "150px", flexShrink: 0 }} value={mapping[i] || ""} onChange={(e) => setMapping((m) => ({ ...m, [i]: e.target.value }))} aria-label={`Column ${i + 1}`}>
                      {MAP_KEYS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            )}
            {bank && bank.code !== "GENERIC" && (
              <label style={{ display: "flex", gap: "0.5rem", alignItems: "center", fontSize: "0.82rem" }}>
                <input type="checkbox" checked={rememberLayout} onChange={(e) => setRememberLayout(e.target.checked)} />
                Remember this layout for {bank?.name || detected.bankName || detected.ifsc.slice(0, 4)} statements
              </label>
            )}
            <div style={{ display: "flex", gap: "0.75rem" }}>
              <Button type="button" variant="secondary" onClick={copyDiagnostics} style={{ flex: 1 }}>{copied ? "✓ Copied" : "📋 Copy details for support"}</Button>
              <Button type="button" variant="primary" onClick={applyMapping} disabled={!candidates.length} style={{ flex: 1 }}>Read with this layout</Button>
            </div>
            <div style={{ fontSize: "0.72rem", color: "var(--text-dim)" }}>"Copy details for support" copies only the page layout — numbers are replaced with # and names are shortened.</div>
          </>
        )}

        {step === "review" && parsed && review && (
          <>
            <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", padding: "0.7rem 0.85rem", fontSize: "0.85rem" }}>
              <div style={{ fontWeight: 700 }}>✓ {target.company.name} • {target.account.bankName} {maskAccountNumber(target.account.accountNumber)}</div>
              <div style={{ color: "var(--text-dim)" }}>{fmt(parsed.period.from)} → {fmt(parsed.period.to)}</div>
              {parsed.hasBalance && <div>Opening {rupees(parsed.opening)} • Closing {rupees(parsed.closing)}</div>}
            </div>
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", fontSize: "0.82rem", fontWeight: 700 }}>
              <span style={{ color: "var(--success)" }}>✓ {selected.length} to import</span>
              <span style={{ color: "var(--text-dim)" }}>↺ {alreadyCount} already imported</span>
              {possibleCount > 0 && <span style={{ color: "var(--warning)" }}>? {possibleCount} possible duplicate{possibleCount === 1 ? "" : "s"}</span>}
            </div>
            {parsed.summaryCheck && (parsed.summaryCheck.ok
              ? <div style={okBox}>✓ Matches the bank's own summary: {parsed.summaryCheck.read.debitCount ?? "?"} debits ({rupees(parsed.summaryCheck.read.debitTotal)}) and {parsed.summaryCheck.read.creditCount ?? "?"} credits ({rupees(parsed.summaryCheck.read.creditTotal)}).</div>
              : <div style={errBox}>⚠️ Doesn't match the bank's summary ({parsed.summaryCheck.mismatches.join(", ")}). Some rows may have been missed — check against the PDF.</div>)}
            {parsed.hasBalance && (parsed.flagged === 0
              ? <div style={okBox}>✓ Balances check out on every row.</div>
              : <div style={errBox}>⚠️ {parsed.flagged} row{parsed.flagged === 1 ? "" : "s"} don't add up (marked below). Check them against the PDF before importing.</div>)}

            <div style={{ maxHeight: "40vh", overflow: "auto", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.75rem" }}>
                <thead>
                  <tr style={{ position: "sticky", top: 0, background: "var(--bg-card)" }}>
                    {["", "Date", "Description", "Debit", "Credit", "Balance"].map((h) => <th key={h} style={{ textAlign: h && h !== "Date" && h !== "Description" ? "right" : "left", padding: "5px" }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {parsed.rows.map((row, i) => {
                    const already = review.existing.has(review.keys[i]);
                    const possible = review.possible.has(i);
                    return (
                      <tr key={i} style={{ opacity: already ? 0.45 : 1, background: row.flag ? "rgba(239,68,68,0.12)" : possible ? "rgba(245,158,11,0.1)" : "transparent", borderTop: "1px solid var(--border-subtle)" }}>
                        <td style={{ padding: "4px 5px", whiteSpace: "nowrap" }}>
                          {already ? "↺" : possible ? (
                            <input type="checkbox" aria-label="Import possible duplicate" checked={includePossible.has(i)} onChange={(e) => setIncludePossible((prev) => { const next = new Set(prev); if (e.target.checked) next.add(i); else next.delete(i); return next; })} />
                          ) : row.flag ? "⚠️" : "✓"}
                        </td>
                        <td style={{ padding: "4px 5px", whiteSpace: "nowrap" }}>{fmt(row.date)}</td>
                        <td style={{ padding: "4px 5px" }}>{row.description}</td>
                        <td style={{ padding: "4px 5px", textAlign: "right", whiteSpace: "nowrap" }}>{row.debitPaise ? rupees(row.debitPaise) : ""}</td>
                        <td style={{ padding: "4px 5px", textAlign: "right", whiteSpace: "nowrap", color: "var(--success)" }}>{row.creditPaise ? rupees(row.creditPaise) : ""}</td>
                        <td style={{ padding: "4px 5px", textAlign: "right", whiteSpace: "nowrap" }}>{rupees(row.balancePaise)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: "0.72rem", color: "var(--text-dim)" }}>↺ already imported (skipped) • ? possible duplicate — tick to import anyway • ⚠️ balance doesn't add up</div>
            <div style={{ display: "flex", gap: "0.75rem" }}>
              <Button type="button" variant="secondary" onClick={onClose} style={{ flex: 1 }}>Cancel</Button>
              <Button type="button" variant="primary" onClick={doImport} loading={busy} disabled={!selected.length} style={{ flex: 1 }}>
                {selected.length ? `Import ${selected.length}` : "Nothing new to import"}
              </Button>
            </div>
          </>
        )}

        {step === "done" && result && (
          <>
            <div style={okBox}>✓ Imported {result.added} transaction{result.added === 1 ? "" : "s"} into {target.company.name} • {target.account.bankName} {maskAccountNumber(target.account.accountNumber)}.{alreadyCount ? ` ${alreadyCount} were already there and were skipped.` : ""}</div>
            <Button type="button" variant="primary" onClick={onClose}>Done</Button>
          </>
        )}
      </div>
    </BottomSheet>
  );
}

export default StatementImportSheet;
